import React from 'react';
import { Calendar, MapPin, TrendingUp, CheckCircle2 } from 'lucide-react';
import './ProjectsPage.css';

const ProjectsPage = () => {
  const projects = [
    {
      title: 'Baltic Platform Expansion',
      status: 'Completed',
      location: 'Baltic Sea',
      year: '2023',
      capacity: '150,000 BPD',
      duration: '18 months',
      image: 'https://images.unsplash.com/photo-1578356058390-f58c575337a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxvZmZzaG9yZSUyMG9pbCUyMHJpZ3xlbnwwfHx8fDE3NjUxMzMzNTR8MA&ixlib=rb-4.1.0&q=85',
      description: 'Expanded offshore platform capacity with advanced drilling technology and enhanced safety systems. Project delivered on time and under budget.',
      objectives: [
        'Increase production capacity by 50%',
        'Implement automated safety protocols',
        'Reduce operational costs by 20%'
      ]
    },
    {
      title: 'Gdansk Refinery Modernization',
      status: 'Completed',
      location: 'Gdansk, Poland',
      year: '2022',
      capacity: '200,000 BPD',
      duration: '24 months',
      image: 'https://images.unsplash.com/photo-1588011930968-eadac80e6a5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxvaWwlMjByZWZpbmVyeXxlbnwwfHx8fDE3NjUxMzMzNDl8MA&ixlib=rb-4.1.0&q=85',
      description: 'Comprehensive modernization of processing units including new catalytic cracking and hydroprocessing capabilities.',
      objectives: [
        'Upgrade refining technology',
        'Improve energy efficiency by 30%',
        'Meet latest environmental standards'
      ]
    },
    {
      title: 'Central European Pipeline Network',
      status: 'Ongoing',
      location: 'Poland-Germany Corridor',
      year: '2024',
      capacity: '800 km',
      duration: '36 months',
      image: 'https://images.unsplash.com/photo-1507823690283-48b0929e727b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxvaWwlMjBwaXBlbGluZXxlbnwwfHx8fDE3NjUxMzMzNjh8MA&ixlib=rb-4.1.0&q=85',
      description: 'Construction of new crude oil pipeline connecting major production areas to refining centers with advanced monitoring systems.',
      objectives: [
        'Expand transportation capacity',
        'Real-time leak detection systems',
        'Minimize environmental impact'
      ]
    },
    {
      title: 'North Sea Offshore Development',
      status: 'Ongoing',
      location: 'Norwegian Sector',
      year: '2024',
      capacity: '120,000 BPD',
      duration: '30 months',
      image: 'https://images.unsplash.com/photo-1690508313456-bf8c851e8319?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxvZmZzaG9yZSUyMG9pbCUyMHJpZ3xlbnwwfHx8fDE3NjUxMzMzNTR8MA&ixlib=rb-4.1.0&q=85',
      description: 'New offshore drilling operations utilizing directional drilling technology to minimize surface footprint.',
      objectives: [
        'Develop new reserves efficiently',
        'Advanced environmental protection',
        'State-of-the-art safety systems'
      ]
    },
    {
      title: 'Storage Terminal Expansion',
      status: 'Completed',
      location: 'Szczecin, Poland',
      year: '2021',
      capacity: '10M Barrels',
      duration: '12 months',
      image: 'https://images.unsplash.com/photo-1559510981-10719ce4266a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHw0fHxvaWwlMjBwaXBlbGluZXxlbnwwfHx8fDE3NjUxMzMzNjh8MA&ixlib=rb-4.1.0&q=85',
      description: 'Expanded strategic storage capacity with modern tank infrastructure and enhanced safety systems.',
      objectives: [
        'Increase storage capacity',
        'Improve loading/unloading efficiency',
        'Enhanced spill prevention systems'
      ]
    },
    {
      title: 'Renewable Energy Integration',
      status: 'Ongoing',
      location: 'Warsaw Region',
      year: '2024',
      capacity: '50 MW Solar',
      duration: '18 months',
      image: 'https://images.pexels.com/photos/3192662/pexels-photo-3192662.jpeg',
      description: 'Integration of solar power generation at refinery sites to reduce carbon footprint and energy costs.',
      objectives: [
        'Generate renewable energy on-site',
        'Reduce carbon emissions by 15%',
        'Lower operational energy costs'
      ]
    }
  ];

  const completedProjects = projects.filter(p => p.status === 'Completed');
  const ongoingProjects = projects.filter(p => p.status === 'Ongoing');

  return (
    <div className="projects-page">
      <section className="projects-hero">
        <div className="projects-hero-overlay"></div>
        <div className="projects-hero-content">
          <h1>Our Projects</h1>
          <p>Delivering excellence in energy infrastructure development</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Completed Projects</h2>
          <p className="section-subtitle">
            Successfully delivered projects demonstrating our execution capabilities
          </p>

          <div className="projects-grid">
            {completedProjects.map((project, index) => (
              <div key={index} className="project-card">
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <span className={`project-status ${project.status.toLowerCase()}`}>
                    <CheckCircle2 size={16} />
                    {project.status}
                  </span>
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <div className="project-meta">
                    <span>
                      <MapPin size={16} />
                      {project.location}
                    </span>
                    <span>
                      <Calendar size={16} />
                      {project.year}
                    </span>
                  </div>
                  <p className="project-description">{project.description}</p>
                  <div className="project-stats">
                    <div className="project-stat">
                      <TrendingUp size={18} />
                      <div>
                        <span className="stat-label">Capacity</span>
                        <span className="stat-value">{project.capacity}</span>
                      </div>
                    </div>
                    <div className="project-stat">
                      <Calendar size={18} />
                      <div>
                        <span className="stat-label">Duration</span>
                        <span className="stat-value">{project.duration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="project-objectives">
                    <h4>Key Objectives</h4>
                    <ul>
                      {project.objectives.map((obj, idx) => (
                        <li key={idx}>{obj}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ backgroundColor: 'var(--light-gray)' }}>
        <div className="container">
          <h2 className="section-title">Ongoing Projects</h2>
          <p className="section-subtitle">
            Current initiatives advancing our operational capabilities
          </p>

          <div className="projects-grid">
            {ongoingProjects.map((project, index) => (
              <div key={index} className="project-card">
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <span className={`project-status ${project.status.toLowerCase()}`}>
                    <TrendingUp size={16} />
                    {project.status}
                  </span>
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <div className="project-meta">
                    <span>
                      <MapPin size={16} />
                      {project.location}
                    </span>
                    <span>
                      <Calendar size={16} />
                      {project.year}
                    </span>
                  </div>
                  <p className="project-description">{project.description}</p>
                  <div className="project-stats">
                    <div className="project-stat">
                      <TrendingUp size={18} />
                      <div>
                        <span className="stat-label">Target</span>
                        <span className="stat-value">{project.capacity}</span>
                      </div>
                    </div>
                    <div className="project-stat">
                      <Calendar size={18} />
                      <div>
                        <span className="stat-label">Timeline</span>
                        <span className="stat-value">{project.duration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="project-objectives">
                    <h4>Project Goals</h4>
                    <ul>
                      {project.objectives.map((obj, idx) => (
                        <li key={idx}>{obj}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;